import {FC, useMemo, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import dayjs from 'dayjs';
import {useIntl} from 'react-intl';
import {AiAgentChartData, AiAgentChartPoint} from 'src/types';
import {chartSx} from './ChatChart.sx';

interface ChatChartProps {
    data: AiAgentChartData;
    // When set (PDF export rig), the chart shows this metric, hides the interactive
    // tabs, and disables animation so its SVG paints immediately for capture.
    forcedMetric?: MetricKey;
}

type Granularity = 'HOURLY' | 'DAILY' | 'MONTHLY';
export type MetricKey = 'kWh' | 'cost' | 'sessions' | 'avgPrice';

// The 4 metric views + their i18n label ids. Shared by the chart, the PDF export rig
// (renders one hidden chart per metric) and the export collector (labels each image).
export const CHART_METRICS: ReadonlyArray<{key: MetricKey; labelId: string}> = [
    {key: 'kWh', labelId: 'ecColumnConsumption'},
    {key: 'cost', labelId: 'ecColumnCost'},
    {key: 'sessions', labelId: 'ecColumnSessions'},
    {key: 'avgPrice', labelId: 'ecColumnAvgPrice'},
];

const inferGranularity = (points: AiAgentChartPoint[]): Granularity => {
    if (points.length < 2) return 'DAILY';
    const deltaMs = points[1].timestamp - points[0].timestamp;
    const hour = 3600 * 1000;
    const day = 24 * hour;
    if (deltaMs < 23 * hour) return 'HOURLY';
    if (deltaMs < 28 * day) return 'DAILY';
    return 'MONTHLY';
};

const formatAxis = (ts: number, gran: Granularity) => {
    const d = dayjs(ts);
    if (gran === 'HOURLY') return d.format('HH:mm');
    if (gran === 'MONTHLY') return d.format('MMM YY');
    return d.format('D MMM');
};

const formatTooltip = (ts: number, gran: Granularity) => {
    const d = dayjs(ts);
    if (gran === 'HOURLY') return d.format('D MMM YYYY, HH:mm');
    if (gran === 'MONTHLY') return d.format('MMMM YYYY');
    return d.format('dddd, D MMMM YYYY');
};

interface RowData {
    timestamp: number;
    name: string;
    kWh: number;
    cost: number;
    sessions: number;
    avgPrice: number;
    anomaly: boolean;
}

interface TooltipPayload {
    payload: RowData;
}

interface MetricConfig {
    key: MetricKey;
    label: string;
    color: string;
    unit: string;
    gradientId: string;
    format: (v: number) => string;
}

const CustomTooltip: FC<{
    active?: boolean;
    payload?: TooltipPayload[];
    granularity: Granularity;
    anomalyLabel: string;
    currency: string;
    activeMetric: MetricKey;
}> = ({active, payload, granularity, anomalyLabel, currency, activeMetric}) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;

    const metricInfo: Record<MetricKey, {label: string; value: string; color: string}> = {
        kWh: {label: 'Energy', value: `${p.kWh.toFixed(2)} kWh`, color: '#007FDC'},
        cost: {label: 'Cost', value: `${p.cost.toFixed(2)} ${currency}`, color: '#399646'},
        sessions: {label: 'Sessions', value: String(p.sessions), color: '#ff7d22'},
        avgPrice: {label: 'Avg Price', value: `${p.avgPrice.toFixed(3)} ${currency}/kWh`, color: '#28419D'},
    };
    const active_ = metricInfo[activeMetric];

    return (
        <Box sx={chartSx.tooltipCard}>
            <Typography sx={chartSx.tooltipDate}>
                {formatTooltip(p.timestamp, granularity)}
            </Typography>
            <Box sx={chartSx.tooltipRow}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                    <Box sx={{...chartSx.legendDot, bgcolor: active_.color}} />
                    <Typography sx={{...chartSx.tooltipLabel, fontWeight: 600, color: 'text.primary'}}>
                        {active_.label}
                    </Typography>
                </Box>
                <Typography sx={chartSx.tooltipValue}>{active_.value}</Typography>
            </Box>
            {p.anomaly && (
                <Box sx={chartSx.tooltipAnomaly}>
                    <span>⚠</span>
                    <span>{anomalyLabel}</span>
                </Box>
            )}
        </Box>
    );
};

const granularityLabelId: Record<Granularity, string> = {
    HOURLY: 'ecGranularityHourly',
    DAILY: 'ecGranularityDaily',
    MONTHLY: 'ecGranularityMonthly',
};

export const ChatChart: FC<ChatChartProps> = ({data, forcedMetric}) => {
    const intl = useIntl();
    const [internalMetric, setActiveMetric] = useState<MetricKey>('kWh');
    const activeMetric = forcedMetric ?? internalMetric;

    const {rows, granularity, totals} = useMemo(() => {
        const pts = (data.points ?? []).filter((p) => p.consumption > 0);
        const gran = (data.granularity as Granularity) ?? inferGranularity(data.points ?? []);
        const r: RowData[] = pts.map((p) => {
            const kWh = p.consumption / 1000;
            const cost = p.cost / 10000;
            const avgPrice = typeof p.averagePrice === 'number'
                ? p.averagePrice / 10000
                : kWh > 0 ? cost / kWh : 0;
            return {
                timestamp: p.timestamp,
                name: formatAxis(p.timestamp, gran),
                kWh,
                cost,
                sessions: p.transactionCount,
                avgPrice,
                anomaly: !!p.anomaly,
            };
        });
        const sumCost = r.reduce((a, x) => a + x.cost, 0);
        const sumKWh = r.reduce((a, x) => a + x.kWh, 0);
        const t = {
            kWh: sumKWh,
            cost: sumCost,
            sessions: r.reduce((a, x) => a + x.sessions, 0),
            avgPrice: sumKWh > 0 ? sumCost / sumKWh : 0,
            anomalies: r.reduce((a, x) => a + (x.anomaly ? 1 : 0), 0),
        };
        return {rows: r, granularity: gran, totals: t};
    }, [data]);

    if (rows.length === 0) return null;

    // Platform default currency for chart cost labels (a per-tenant currency isn't
    // carried on the chart payload today).
    const currency = 'CHF';
    const title = data.title ?? intl.formatMessage({id: 'ecConsumptionOverTime'});
    const granularityText = intl.formatMessage({id: granularityLabelId[granularity]});

    const metrics: MetricConfig[] = [
        {
            key: 'kWh',
            label: intl.formatMessage({id: 'ecColumnConsumption'}),
            color: '#007FDC',
            unit: ' kWh',
            gradientId: 'chatChartGradKwh',
            format: (v) => `${v.toFixed(2)} kWh`,
        },
        {
            key: 'cost',
            label: intl.formatMessage({id: 'ecColumnCost'}),
            color: '#399646',
            unit: ` ${currency}`,
            gradientId: 'chatChartGradCost',
            format: (v) => `${v.toFixed(2)} ${currency}`,
        },
        {
            key: 'sessions',
            label: intl.formatMessage({id: 'ecColumnSessions'}),
            color: '#ff7d22',
            unit: '',
            gradientId: 'chatChartGradSessions',
            format: (v) => String(Math.round(v)),
        },
        {
            key: 'avgPrice',
            label: intl.formatMessage({id: 'ecColumnAvgPrice'}),
            color: '#28419D',
            unit: '',
            gradientId: 'chatChartGradAvgPrice',
            format: (v) => `${v.toFixed(3)}`,
        },
    ];

    const currentMetric = metrics.find((m) => m.key === activeMetric) ?? metrics[0];
    const totalLabel = activeMetric === 'kWh' ? 'Total' : activeMetric === 'sessions' ? 'Total' : activeMetric === 'avgPrice' ? 'Average' : 'Cost';
    const totalDisplay =
        activeMetric === 'kWh' ? `${totals.kWh.toFixed(1)} kWh` :
        activeMetric === 'cost' ? `${totals.cost.toFixed(2)} ${currency}` :
        activeMetric === 'sessions' ? `${totals.sessions}` :
        `${totals.avgPrice.toFixed(3)} ${currency}/kWh`;

    return (
        // `data-ai-chart` marks this chart for PDF export: the capture util collects
        // every `[data-ai-chart] svg` in document order, which matches the chart order
        // the export collector sees (messages top-to-bottom, charts in array order).
        <Box sx={chartSx.root} data-ai-chart>
            <Box sx={chartSx.header}>
                <Box sx={chartSx.titleWrap}>
                    <Typography sx={chartSx.subtitle}>{granularityText}</Typography>
                    <Typography sx={chartSx.title}>{title}</Typography>
                </Box>
                <Box sx={chartSx.totals}>
                    <Box sx={chartSx.totalItem}>
                        <Typography sx={chartSx.totalLabel}>{totalLabel}</Typography>
                        <Typography sx={{...chartSx.totalValue, color: currentMetric.color}}>
                            {totalDisplay}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {!forcedMetric && (
                <Box sx={chartSx.metricTabs}>
                    {metrics.map((m) => {
                        const isActive = m.key === activeMetric;
                        return (
                            <Box
                                key={m.key}
                                sx={isActive
                                    ? {...chartSx.metricTabActive, bgcolor: m.color}
                                    : chartSx.metricTab}
                                onClick={() => setActiveMetric(m.key)}
                            >
                                {m.label}
                            </Box>
                        );
                    })}
                </Box>
            )}

            <Box sx={chartSx.chartWrapper}>
                <Box sx={chartSx.chartInner}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={rows} margin={{top: 8, right: 10, left: -8, bottom: 0}}>
                        <defs>
                            <linearGradient id={currentMetric.gradientId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={currentMetric.color} stopOpacity={0.35} />
                                <stop offset="100%" stopColor={currentMetric.color} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
                        <XAxis
                            dataKey="name"
                            fontSize={10}
                            tickLine={false}
                            axisLine={false}
                            stroke="#9090B0"
                            interval="preserveStartEnd"
                        />
                        <YAxis
                            fontSize={10}
                            tickLine={false}
                            axisLine={false}
                            stroke="#9090B0"
                            unit={currentMetric.unit}
                            width={72}
                        />
                        <Tooltip
                            content={
                                <CustomTooltip
                                    granularity={granularity}
                                    anomalyLabel={intl.formatMessage({id: 'ecAnomaly'})}
                                    currency={currency}
                                    activeMetric={activeMetric}
                                />
                            }
                            cursor={{stroke: `${currentMetric.color}30`, strokeWidth: 2}}
                        />
                        <Area
                            type="monotone"
                            dataKey={activeMetric}
                            isAnimationActive={!forcedMetric}
                            stroke={currentMetric.color}
                            strokeWidth={2}
                            fill={`url(#${currentMetric.gradientId})`}
                            dot={(props) => {
                                const {cx, cy, payload, index} = props;
                                if (!payload.anomaly) {
                                    return <circle key={`dot-${index}`} cx={cx} cy={cy} r={0} />;
                                }
                                return (
                                    <circle
                                        key={`anomaly-${index}`}
                                        cx={cx}
                                        cy={cy}
                                        r={4}
                                        fill="#e62930"
                                        stroke="white"
                                        strokeWidth={2}
                                    />
                                );
                            }}
                            activeDot={{r: 4, fill: currentMetric.color, stroke: 'white', strokeWidth: 2}}
                        />
                    </AreaChart>
                </ResponsiveContainer>
                </Box>
            </Box>

            <Box sx={chartSx.footer}>
                <Box sx={chartSx.footerItem}>
                    <Box component="span" sx={chartSx.footerLabel}>{intl.formatMessage({id: 'ecColumnConsumption'})}:</Box>
                    <Box component="span" sx={chartSx.footerValue}>{totals.kWh.toFixed(1)} kWh</Box>
                </Box>
                <Box sx={chartSx.footerItem}>
                    <Box component="span" sx={chartSx.footerLabel}>{intl.formatMessage({id: 'ecColumnCost'})}:</Box>
                    <Box component="span" sx={chartSx.footerValue}>{totals.cost.toFixed(2)} {currency}</Box>
                </Box>
                <Box sx={chartSx.footerItem}>
                    <Box component="span" sx={chartSx.footerLabel}>{intl.formatMessage({id: 'ecColumnSessions'})}:</Box>
                    <Box component="span" sx={chartSx.footerValue}>{totals.sessions}</Box>
                </Box>
                <Box sx={chartSx.footerItem}>
                    <Box component="span" sx={chartSx.footerLabel}>{intl.formatMessage({id: 'ecColumnAvgPrice'})}:</Box>
                    <Box component="span" sx={chartSx.footerValue}>{totals.avgPrice.toFixed(3)} {currency}/kWh</Box>
                </Box>
            </Box>
        </Box>
    );
};